import { wrapRecordFn, makeSortFn, fetchJsonApi } from "./jsonApi";

export class JsonApiCollection {
  constructor(response) {
    if (response instanceof JsonApiCollection) {
      this._links = response._links
      this._meta = response._meta
      this._data = response._data
      this._included = response._included
      this._sortFn = response._sortFn
      this._filterFn = response._filterFn
      this._wrap = wrapRecordFn(this)
    } else {
      this.reset(response)
    }
  }
  get size() {
    return this._data.length
  }
  reset(response) {
    this._links = Object.fromEntries(
      Object.entries(response.links || {}).map(([k, v]) => [k, new URL(v)])
    );
    this._meta = response.meta || {}
    this._data = response.data || []
    this._included = response.included || []
    this._sortFn = makeSortFn(this._links.self?.searchParams?.get('sort'));
    this._wrap = wrapRecordFn(this);
    return this;
  }
  _guessType() {
    // Considering the URL for this collection...
    return this._links.self.pathname. // Look at just the path, no domain or query params
      split("/"). // Split path into pieces
      pop(). // Look at just the last piece
      replace(/s?(\.w+)?$/,'') // Remove trailing 's' and file extension if any
  }
  async create(attributes, options = {}) {
    const response = await fetchJsonApi(this._links.self, {
      method: "POST",
      body: JSON.stringify({
        data: {
          type: options.type || this._meta.default_type || this._guessType(),
          attributes
        }
      })
    });
    if (response.errors) throw response;
    return this.addResult(response);
  }
  get(recordIdentifier) {
    const [record, included] = this._getRecordIncluded(recordIdentifier);
    if (!record) return record;
    const wrapped = this._wrap(record, included);
    if (included || !this._filterFn || this._filterFn(wrapped)) return wrapped;
    return null;
  }
  _getRecordIncluded(recordIdentifier) {
    const { id, type } = recordIdentifier;
    const data = this._data.find(r => r.id == id && r.type == type);
    if (data) return [data, false];
    const included = this._included.find(r => r.id == id && r.type == type);
    if (included) return [included, true];
    return [null, false];
  }
  delete(recordIdentifier, included=false) {
    const { id, type } = recordIdentifier;
    const array = included ? this._included : this._data;
    const index = array.findIndex(r => r.id == id && r.type == type);
    if (index === -1) return false;
    array.splice(index, 1);
    return true;
  }
  add(record, included=false) {
    const { id, type } = record;
    const array = included ? this._included : this._data;
    const index = array.findIndex(r => r.id == id && r.type == type);
    if (index === -1) {
      array.push(record);
    } else {
      array[index] = record;
    }
    if(!included) this._sort();
    return this._wrap(record, included);
  }
  addResult(result, included=false) {
    for (const record of result.included || []) {
      // TODO: Technically an included object update could return includes
      // that are plain data for the collection, so this is incorrect. So far
      // we don't call actions on included records at all, though.
      this.add(record, true);
    }
    if (result.data) return this.add(result.data, included);
  }
  filter(fn) {
    const filtered = new this.constructor(this)
    const existingFn = this._filterFn
    const filterFn = existingFn ? v => existingFn(v) && fn(v) : fn
    filtered._filterFn = filterFn
    return filtered
  }
  _sort() {
    this._data.sort(this._sortFn);
  }
  entries() {
    return this;
  }
  *[Symbol.iterator]() {
    if (this._filterFn) {
      for (const record of this._data) {
        const r = this._wrap(record);
        if (this._filterFn(r)) yield r;
      }
    } else {
      for (const record of this._data) {
        yield this._wrap(record);
      }
    }
  }
}

export default JsonApiCollection